import { BaseRestRepository } from './baseRestRepository';
import type {
  CopyLeaseTemplateDto,
  LeaseTemplateCategoryDto,
  LeaseTemplateDto,
  LeaseTemplateType,
  UpdateLeaseTemplateDto,
} from '~/swagger/portal/Api';

const LEASE_TEMPLATE_URL = '/api/lease-templates';

export interface CreateLeaseTemplateDto {
  categoryId: string;
  file: File;
  name: string;
  description?: string;
  type?: `${LeaseTemplateType}`;
}

export class LeaseTemplateRepository extends BaseRestRepository {
  public async getLeaseTemplates(): Promise<LeaseTemplateDto[]> {
    const response = await this.fetchWithAuth(LEASE_TEMPLATE_URL);
    return this.getJsonResponse(response);
  }

  public async getLeaseTemplate(id: string): Promise<LeaseTemplateDto> {
    const url = `${LEASE_TEMPLATE_URL}/${id}`;

    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createLeaseTemplate(params: CreateLeaseTemplateDto): Promise<LeaseTemplateDto> {
    const formData = new FormData();
    formData.append('LeaseTemplateCategoryId', params.categoryId);
    formData.append('Name', params.name);
    const { compressPdfImages } = await import('~/pdfsigner/repositories/pdfImageCompressor');
    const compressedFile = await compressPdfImages(params.file);
    formData.append('File', compressedFile);
    formData.append('Description', params.description || '');
    formData.append('Type', params.type || '');

    const response = await this.fetchWithAuth(LEASE_TEMPLATE_URL, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return this.getJsonResponse(response);
  }

  public async updateLeaseTemplate(id: string, payload: UpdateLeaseTemplateDto): Promise<LeaseTemplateDto> {
    const url = `${LEASE_TEMPLATE_URL}/${id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteLeaseTemplate(id: string): Promise<void> {
    const url = `${LEASE_TEMPLATE_URL}/${id}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async copyLeaseTemplate(payload: CopyLeaseTemplateDto): Promise<void> {
    const url = `${LEASE_TEMPLATE_URL}/copy`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async addLeaseTemplateAttachment(id: string, file: File): Promise<LeaseTemplateDto> {
    const url = `${LEASE_TEMPLATE_URL}/${id}/file`;
    const { compressPdfImages } = await import('~/pdfsigner/repositories/pdfImageCompressor');
    const compressedFile = await compressPdfImages(file);
    const formData = new FormData();
    formData.append('File', compressedFile);

    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return this.getJsonResponse(response);
  }

  public async getLeaseTemplateCategories(): Promise<LeaseTemplateCategoryDto[]> {
    const url = `${LEASE_TEMPLATE_URL}/categories`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async isPdfFileEncrypted(file: File): Promise<boolean> {
    const { PDFDocument } = await import('pdf-lib');
    const arrayBuffer = await file.arrayBuffer();
    const pdfBytes = new Uint8Array(arrayBuffer);
    const document = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    return document.isEncrypted;
  }
}

export const leaseTemplateRepo = new LeaseTemplateRepository();
